// ============================================================
// Xap Xam Card Game — Game Engine
// Pure functions: deck, shuffle, deal, hand evaluation,
// comparison, scoring for all 4 modes
// ============================================================

import {
  RANKS, SUITS, RANK_VALUES, HAND_TYPES, TOP_HAND_TYPES,
  CHI_BONUSES, HAND_SIZE, BOTTOM_SIZE, MIDDLE_SIZE, TOP_SIZE,
  DECK_SCALING, GAME_MODES,
} from './gameConfig';

// ============================================================
// DECK MANAGEMENT
// ============================================================

/**
 * Create a single 52-card deck.
 * @param {number} deckIndex - Which deck this is (for multi-deck identification)
 * @returns {Array} Array of card objects { rank, suit, deckIndex, id }
 */
export function createDeck(deckIndex = 0) {
  const deck = [];
  for (const suit of SUITS) {
    for (const rank of RANKS) {
      deck.push({
        rank,
        suit,
        deckIndex,
        id: `${rank}${suit}_${deckIndex}`, // Unique ID for React keys
      });
    }
  }
  return deck;
}

/**
 * Create multiple decks combined.
 * @param {number} count - Number of decks (1-5)
 * @returns {Array} Combined array of cards
 */
export function createMultiDeck(count = 1) {
  const cards = [];
  for (let i = 0; i < count; i++) {
    cards.push(...createDeck(i));
  }
  return cards;
}

/**
 * Calculate the minimum number of decks needed for a player count.
 * @param {number} playerCount
 * @returns {number} Minimum deck count
 */
export function getMinDecks(playerCount) {
  if (playerCount >= DECK_SCALING.THIRD_DECK_AT) return 3;
  if (playerCount >= DECK_SCALING.SECOND_DECK_AT) return 2;
  return 1;
}

/**
 * Fisher-Yates shuffle (cryptographically random when available).
 * @param {Array} arr - Array to shuffle (mutated in place)
 * @returns {Array} The shuffled array
 */
export function shuffle(arr) {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/**
 * Deal cards to players.
 * @param {number} playerCount - Number of players to deal to
 * @param {number} deckCount - Number of decks to use
 * @param {number} cardsPerPlayer - Cards per player (default 13)
 * @returns {{ hands: Array<Array>, remaining: Array }} Dealt hands and remaining deck
 */
export function deal(playerCount, deckCount = 1, cardsPerPlayer = HAND_SIZE) {
  const deck = shuffle(createMultiDeck(deckCount));
  const totalNeeded = playerCount * cardsPerPlayer;

  if (totalNeeded > deck.length) {
    throw new Error(
      `Not enough cards: need ${totalNeeded} but only have ${deck.length}`
    );
  }

  const hands = [];
  for (let p = 0; p < playerCount; p++) {
    hands.push(deck.splice(0, cardsPerPlayer));
  }

  return { hands, remaining: deck };
}

// ============================================================
// CARD UTILITIES
// ============================================================

/** Get the numeric value of a card's rank (2-14, where A=14). */
export function rankValue(card) {
  return RANK_VALUES[card.rank];
}

/** Sort cards by rank value descending (highest first). */
export function sortByRankDesc(cards) {
  return [...cards].sort((a, b) => rankValue(b) - rankValue(a));
}

/** Sort cards by rank value ascending, then by suit. */
export function sortByRankAsc(cards) {
  return [...cards].sort((a, b) => {
    const rv = rankValue(a) - rankValue(b);
    if (rv !== 0) return rv;
    return SUITS.indexOf(a.suit) - SUITS.indexOf(b.suit);
  });
}

/** Sort cards by suit, then by rank within each suit. */
export function sortBySuit(cards) {
  return [...cards].sort((a, b) => {
    const si = SUITS.indexOf(a.suit) - SUITS.indexOf(b.suit);
    if (si !== 0) return si;
    return rankValue(a) - rankValue(b);
  });
}

/** Group cards by rank. Returns Map<rankValue, cards[]>. */
function groupByRank(cards) {
  const groups = new Map();
  for (const card of cards) {
    const rv = rankValue(card);
    if (!groups.has(rv)) groups.set(rv, []);
    groups.get(rv).push(card);
  }
  return groups;
}

/** Check if all cards share the same suit (ignoring deckIndex). */
function isFlush(cards) {
  return cards.every(c => c.suit === cards[0].suit);
}

/**
 * Check if cards form a straight. Returns the high card value, or 0 if not a straight.
 * Handles A-2-3-4-5 (returns 5) and 10-J-Q-K-A (returns 14).
 */
function getStraightHigh(cards) {
  const values = [...new Set(cards.map(c => rankValue(c)))].sort((a, b) => a - b);
  if (values.length !== cards.length) return 0; // Duplicates = not a straight

  // Check normal straight
  const isConsecutive = values.every((v, i) => i === 0 || v === values[i - 1] + 1);
  if (isConsecutive) return values[values.length - 1];

  // Check A-2-3-4-5 (ace-low straight / wheel)
  if (values.length === 5) {
    const wheel = [2, 3, 4, 5, 14]; // A=14
    if (values.every((v, i) => v === wheel[i])) return 5; // High card is 5
  }

  return 0;
}

// ============================================================
// HAND EVALUATION — 5-CARD HANDS (Bottom & Middle)
// ============================================================

/**
 * Evaluate a 5-card hand.
 * @param {Array} cards - Exactly 5 cards
 * @returns {{ type: number, kickers: number[] }}
 *   type: HAND_TYPES value (0-10)
 *   kickers: array of rank values for tie-breaking (descending)
 */
export function evaluate5(cards) {
  if (cards.length !== 5) throw new Error(`Expected 5 cards, got ${cards.length}`);

  const groups = groupByRank(cards);
  const counts = [...groups.entries()]
    .map(([rv, c]) => ({ rv, count: c.length }))
    .sort((a, b) => b.count - a.count || b.rv - a.rv);

  const flush = isFlush(cards);
  const straightHigh = getStraightHigh(cards);
  const straight = straightHigh > 0;

  // --- 5 of a kind (multi-deck only) ---
  if (counts[0].count === 5) {
    return { type: HAND_TYPES.FIVE_OF_A_KIND, kickers: [counts[0].rv] };
  }

  // --- Royal flush ---
  if (flush && straight && straightHigh === 14) {
    return { type: HAND_TYPES.ROYAL_FLUSH, kickers: [14] };
  }

  // --- Straight flush ---
  if (flush && straight) {
    return { type: HAND_TYPES.STRAIGHT_FLUSH, kickers: [straightHigh] };
  }

  // --- Four of a kind ---
  if (counts[0].count === 4) {
    const kicker = counts[1].rv;
    return { type: HAND_TYPES.FOUR_OF_A_KIND, kickers: [counts[0].rv, kicker] };
  }

  // --- Full house ---
  if (counts[0].count === 3 && counts[1].count === 2) {
    return { type: HAND_TYPES.FULL_HOUSE, kickers: [counts[0].rv, counts[1].rv] };
  }

  // --- Flush ---
  if (flush) {
    const kickers = sortByRankDesc(cards).map(c => rankValue(c));
    return { type: HAND_TYPES.FLUSH, kickers };
  }

  // --- Straight ---
  if (straight) {
    return { type: HAND_TYPES.STRAIGHT, kickers: [straightHigh] };
  }

  // --- Three of a kind ---
  if (counts[0].count === 3) {
    const kickers = [counts[0].rv, counts[1].rv, counts[2].rv];
    return { type: HAND_TYPES.THREE_OF_A_KIND, kickers };
  }

  // --- Two pair ---
  if (counts[0].count === 2 && counts[1].count === 2) {
    const pairs = [counts[0].rv, counts[1].rv].sort((a, b) => b - a);
    return { type: HAND_TYPES.TWO_PAIR, kickers: [...pairs, counts[2].rv] };
  }

  // --- One pair ---
  if (counts[0].count === 2) {
    const kickers = [counts[0].rv, ...counts.slice(1).map(c => c.rv).sort((a, b) => b - a)];
    return { type: HAND_TYPES.ONE_PAIR, kickers };
  }

  // --- High card ---
  const kickers = sortByRankDesc(cards).map(c => rankValue(c));
  return { type: HAND_TYPES.HIGH_CARD, kickers };
}

// ============================================================
// HAND EVALUATION — 3-CARD HANDS (Top Row)
// ============================================================

/**
 * Evaluate a 3-card hand (top row).
 * No straights or flushes count.
 * @param {Array} cards - Exactly 3 cards
 * @returns {{ type: number, kickers: number[] }}
 */
export function evaluate3(cards) {
  if (cards.length !== 3) throw new Error(`Expected 3 cards, got ${cards.length}`);

  const groups = groupByRank(cards);
  const counts = [...groups.entries()]
    .map(([rv, c]) => ({ rv, count: c.length }))
    .sort((a, b) => b.count - a.count || b.rv - a.rv);

  // --- Three of a kind ---
  if (counts[0].count === 3) {
    return { type: TOP_HAND_TYPES.THREE_OF_A_KIND, kickers: [counts[0].rv] };
  }

  // --- One pair ---
  if (counts[0].count === 2) {
    return { type: TOP_HAND_TYPES.ONE_PAIR, kickers: [counts[0].rv, counts[1].rv] };
  }

  // --- High card ---
  const kickers = sortByRankDesc(cards).map(c => rankValue(c));
  return { type: TOP_HAND_TYPES.HIGH_CARD, kickers };
}

// ============================================================
// HAND COMPARISON
// ============================================================

/**
 * Compare two evaluated hands.
 * @returns {number} +1 if hand1 wins, -1 if hand2 wins, 0 if tie
 */
export function compareHands(eval1, eval2) {
  // Compare hand type first
  if (eval1.type !== eval2.type) {
    return eval1.type > eval2.type ? 1 : -1;
  }

  // Compare kickers (next highest card tie-breaking)
  for (let i = 0; i < Math.min(eval1.kickers.length, eval2.kickers.length); i++) {
    if (eval1.kickers[i] !== eval2.kickers[i]) {
      return eval1.kickers[i] > eval2.kickers[i] ? 1 : -1;
    }
  }

  // All identical = push
  return 0;
}

// ============================================================
// ARRANGEMENT VALIDATION
// ============================================================

/**
 * Check if a full 13-card arrangement is valid (Bottom ≥ Middle ≥ Top).
 * @param {{ bottom: Array, middle: Array, top: Array }} arrangement
 * @returns {{ valid: boolean, violations: string[] }}
 */
export function validateArrangement(arrangement) {
  const { bottom, middle, top } = arrangement;
  const violations = [];

  if (bottom.length !== BOTTOM_SIZE) violations.push(`Bottom must have ${BOTTOM_SIZE} cards`);
  if (middle.length !== MIDDLE_SIZE) violations.push(`Middle must have ${MIDDLE_SIZE} cards`);
  if (top.length !== TOP_SIZE) violations.push(`Top must have ${TOP_SIZE} cards`);

  if (violations.length > 0) return { valid: false, violations, fullySet: false };

  const bottomEval = evaluate5(bottom);
  const middleEval = evaluate5(middle);
  const topEval = evaluate3(top);

  // Bottom must be ≥ Middle
  const bmCompare = compareHands(bottomEval, middleEval);
  if (bmCompare < 0) {
    violations.push('Bottom must be stronger than or equal to Middle');
  }

  // Middle must be ≥ Top (cross-type comparison: use raw type ordering)
  // Since Middle is a 5-card eval and Top is a 3-card eval,
  // we need special comparison. A 5-card hand is always compared by its
  // type in the unified ranking. 3-card three-of-a-kind < any 5-card straight or better.
  // The rule is simply: Middle hand rank ≥ Top hand rank in poker terms.
  const middleStrength = getUnifiedStrength(middleEval, 5);
  const topStrength = getUnifiedStrength(topEval, 3);

  if (middleStrength < topStrength) {
    violations.push('Middle must be stronger than or equal to Top');
  } else if (middleStrength === topStrength) {
    // Same category — compare kickers
    for (let i = 0; i < Math.min(middleEval.kickers.length, topEval.kickers.length); i++) {
      if (middleEval.kickers[i] < topEval.kickers[i]) {
        violations.push('Middle must be stronger than or equal to Top');
        break;
      } else if (middleEval.kickers[i] > topEval.kickers[i]) {
        break;
      }
    }
  }

  return {
    valid: violations.length === 0,
    violations,
    fullySet: true,
    bottomEval,
    middleEval,
    topEval,
  };
}

/**
 * Get a unified strength value for cross-row comparison (Middle 5-card vs Top 3-card).
 * Maps hand types to a comparable scale:
 *   High card = 0, One pair = 1, Two pair = 2, Three of a kind = 3, etc.
 * For 3-card hands: high card = 0, one pair = 1, three of a kind = 3.
 */
function getUnifiedStrength(evalResult, cardCount) {
  if (cardCount === 3) {
    // Map 3-card types to unified scale
    if (evalResult.type === TOP_HAND_TYPES.THREE_OF_A_KIND) return 3;
    if (evalResult.type === TOP_HAND_TYPES.ONE_PAIR) return 1;
    return 0; // HIGH_CARD
  }
  // 5-card types already use HAND_TYPES which is 0-10
  return evalResult.type;
}

/**
 * Check if hand is fully set (all 13 cards placed in rows).
 */
export function isHandFullySet(arrangement) {
  const { bottom, middle, top } = arrangement;
  return (
    bottom.length === BOTTOM_SIZE &&
    middle.length === MIDDLE_SIZE &&
    top.length === TOP_SIZE
  );
}

/**
 * Get violation rows for violation help (returns which rows are in violation).
 * Only checks when hand is fully set.
 */
export function getViolationRows(arrangement) {
  if (!isHandFullySet(arrangement)) return [];

  const result = validateArrangement(arrangement);
  if (result.valid) return [];

  const rows = [];
  if (result.violations.some(v => v.includes('Bottom') && v.includes('Middle'))) {
    rows.push('bottom', 'middle');
  }
  if (result.violations.some(v => v.includes('Middle') && v.includes('Top'))) {
    rows.push('middle', 'top');
  }
  return [...new Set(rows)];
}

// ============================================================
// SPECIAL 13-CARD HANDS (Basic mode auto-wins)
// ============================================================

/**
 * Check if 13 cards form a special auto-win hand.
 * @param {Array} cards - All 13 cards (before arrangement)
 * @returns {{ special: string|null, name: string|null }}
 */
export function checkSpecialHand(cards) {
  if (cards.length !== 13) return { special: null, name: null };

  // --- Dragon: all 13 ranks A-K (one of each) ---
  const uniqueRanks = new Set(cards.map(c => c.rank));
  if (uniqueRanks.size === 13) {
    return { special: 'dragon', name: 'Dragon (Complete Straight)' };
  }

  // --- Six pairs: exactly 6 distinct rank pairs + 1 odd card ---
  // Four-of-a-kind does NOT count toward Six Pairs — only ranks with exactly 2 cards count
  const groups = groupByRank(cards);
  let pairCount = 0;
  for (const [, group] of groups) {
    if (group.length === 2) pairCount += 1; // only exact pairs, not four-of-a-kind
  }
  if (pairCount === 6) {
    return { special: 'six_pairs', name: 'Six Pairs' };
  }

  // --- Three straights: bottom 5-card straight, middle 5-card straight, top 3-card straight ---
  // This requires checking if the 13 cards can be arranged into 3 straights.
  // Since the player must declare it before arranging, we check all possible arrangements.
  if (canFormThreeStraights(cards)) {
    return { special: 'three_straights', name: 'Three Straights' };
  }

  // --- Three flushes: bottom 5-card flush, middle 5-card flush, top 3-card flush ---
  if (canFormThreeFlushes(cards)) {
    return { special: 'three_flushes', name: 'Three Flushes' };
  }

  return { special: null, name: null };
}

/**
 * Check if 13 cards can be split into 3 straights (5 + 5 + 3).
 * A 3-card straight is: consecutive ranks (e.g., 4-5-6, Q-K-A, A-2-3).
 */
function canFormThreeStraights(cards) {
  // This is computationally expensive to check exhaustively.
  // We'll use a recursive backtracking approach.
  const sorted = sortByRankAsc(cards);
  return findThreeStraights(sorted.map(c => rankValue(c)), [5, 5, 3], 0);
}

function findThreeStraights(values, sizes, sizeIdx) {
  if (sizeIdx >= sizes.length) return values.length === 0;
  const size = sizes[sizeIdx];
  const remaining = [...values];

  // Try all combinations of `size` consecutive values from remaining
  return tryConsecutive(remaining, size, (rest) => {
    return findThreeStraights(rest, sizes, sizeIdx + 1);
  });
}

function tryConsecutive(values, length, callback) {
  if (values.length < length) return false;
  const unique = [...new Set(values)].sort((a, b) => a - b);

  for (let start = 0; start <= unique.length - length; start++) {
    const seq = unique.slice(start, start + length);
    // Check if consecutive
    if (seq[seq.length - 1] - seq[0] === length - 1) {
      const rest = removeValues(values, seq);
      if (callback(rest)) return true;
    }
  }

  // Also check ace-low for 5-card: A-2-3-4-5
  if (length === 5 && unique.includes(14)) {
    const aceLow = [2, 3, 4, 5, 14];
    if (aceLow.every(v => values.includes(v))) {
      const rest = removeValues(values, aceLow);
      if (callback(rest)) return true;
    }
  }

  // Ace-low for 3-card: A-2-3
  if (length === 3 && unique.includes(14)) {
    const aceLow3 = [2, 3, 14];
    if (aceLow3.every(v => values.includes(v))) {
      const rest = removeValues(values, aceLow3);
      if (callback(rest)) return true;
    }
  }

  return false;
}

function removeValues(arr, toRemove) {
  const result = [...arr];
  for (const v of toRemove) {
    const idx = result.indexOf(v);
    if (idx >= 0) result.splice(idx, 1);
  }
  return result;
}

/**
 * Check if 13 cards can be split into 3 flushes (5 + 5 + 3).
 * Each group must be the same suit.
 */
function canFormThreeFlushes(cards) {
  const bySuit = {};
  for (const card of cards) {
    if (!bySuit[card.suit]) bySuit[card.suit] = 0;
    bySuit[card.suit]++;
  }

  const counts = Object.values(bySuit).sort((a, b) => b - a);

  // Need at least 3 of one suit, and suits that can form 5+5+3
  // Possible distributions: 5+5+3, 5+3+3+2 (no), 8+5, 8+3+2 (no), etc.
  // Valid: any way to pick groups of 5, 5, 3 where each group is single suit.
  // This means we need suits with counts that can sum to 5, 5, and 3.
  // Simplest check: can we partition suit counts into groups summing to 5, 5, 3?
  // Since each flush group must be ONE suit (not mixed), each suit contributes
  // entirely to one group. A suit with 8 cards can give 5 to one group and 3 to another.

  // Actually, for a flush, ALL cards in the row must be the same suit.
  // So we need: one suit with ≥5 (for bottom), another (or same) with ≥5 (for middle),
  // and one with ≥3 (for top). But suits can't overlap in the same row.
  // A suit with 10 cards could provide both a 5-card bottom and a 5-card middle.

  // Check all valid partitions of suit counts into exactly 5+5+3
  return canPartitionFlushes(counts, [5, 5, 3], 0);
}

function canPartitionFlushes(suitCounts, targets, targetIdx) {
  if (targetIdx >= targets.length) {
    return suitCounts.every(c => c === 0);
  }
  const need = targets[targetIdx];

  for (let i = 0; i < suitCounts.length; i++) {
    if (suitCounts[i] >= need) {
      const next = [...suitCounts];
      next[i] -= need;
      if (canPartitionFlushes(next, targets, targetIdx + 1)) return true;
    }
  }
  return false;
}

// ============================================================
// AUTO-WIN ARRANGEMENT VALIDATION
// ============================================================

/**
 * Validate that a fully-set arrangement matches the auto-win special hand type.
 * Player must arrange cards in the correct format before the "Show Auto Win" button appears.
 *
 * @param {{ bottom: Array, middle: Array, top: Array }} arrangement
 * @param {string} specialType — 'dragon' | 'six_pairs' | 'three_straights' | 'three_flushes'
 * @returns {boolean}
 */
export function validateAutoWinArrangement(arrangement, specialType) {
  if (!isHandFullySet(arrangement)) return false;

  switch (specialType) {
    case 'dragon':
      // Dragon: all 13 unique ranks present — just needs to be fully set
      return true;

    case 'six_pairs': {
      // Six Pairs: exactly 6 ranks with exactly 2 cards each (four-of-a-kind does not count)
      const allCards = [...arrangement.bottom, ...arrangement.middle, ...arrangement.top];
      const groups = new Map();
      for (const card of allCards) {
        const rv = rankValue(card);
        groups.set(rv, (groups.get(rv) || 0) + 1);
      }
      let pairCount = 0;
      for (const count of groups.values()) {
        if (count === 2) pairCount += 1;
      }
      return pairCount >= 6;
    }

    case 'three_straights': {
      // Three Straights: bottom + middle are 5-card straights, top 3 cards are consecutive
      const bottomStraight = getStraightHigh(arrangement.bottom) > 0;
      const middleStraight = getStraightHigh(arrangement.middle) > 0;
      const topValues = arrangement.top.map(c => rankValue(c)).sort((a, b) => a - b);
      let topStraight = false;
      if (topValues.length === 3) {
        // Normal consecutive
        if (topValues[2] - topValues[0] === 2 && new Set(topValues).size === 3) {
          topStraight = true;
        }
        // Ace-low: A-2-3
        if (topValues[0] === 2 && topValues[1] === 3 && topValues[2] === 14) {
          topStraight = true;
        }
        // Ace-high: Q-K-A
        if (topValues[0] === 12 && topValues[1] === 13 && topValues[2] === 14) {
          topStraight = true;
        }
      }
      return bottomStraight && middleStraight && topStraight;
    }

    case 'three_flushes': {
      // Three Flushes: bottom + middle are flushes, top 3 cards share a suit
      const bottomFlush = arrangement.bottom.length === 5 && arrangement.bottom.every(c => c.suit === arrangement.bottom[0].suit);
      const middleFlush = arrangement.middle.length === 5 && arrangement.middle.every(c => c.suit === arrangement.middle[0].suit);
      const topFlush = arrangement.top.length === 3 && arrangement.top.every(c => c.suit === arrangement.top[0].suit);
      return bottomFlush && middleFlush && topFlush;
    }

    default:
      return false;
  }
}

// ============================================================
// SCORING — BASIC / NEM (Majority-based)
// ============================================================

/**
 * Score a round in Basic or Nem mode (majority-based).
 * Compare 3 rows vs each opponent. Win majority = +1, lose = -1, tie = 0.
 *
 * @param {Object} arrangements - { uid: { bottom, middle, top } }
 * @param {Object} activeRules - { sweepBonus, individualRoundHouse, autoWinEnabled, autoWinValue }
 * @param {Object} originalHands - { uid: cards[] } — for auto-win validation
 * @returns {Object} { uid: { total, breakdown: { [opponentUid]: { rows: [1,-1,1], result, points } } } }
 */
export function scoreBasicNem(arrangements, activeRules = {}, originalHands = {}) {
  const uids = Object.keys(arrangements);
  const results = {};

  // Initialize results
  for (const uid of uids) {
    results[uid] = { total: 0, breakdown: {} };
  }

  // Check for auto-wins (only on original hands)
  const autoWinners = new Set();
  if (activeRules.autoWinEnabled) {
    for (const uid of uids) {
      const original = originalHands[uid];
      if (original && checkSpecialHand(original).special) {
        autoWinners.add(uid);
      }
    }
  }

  // Evaluate all hands
  const evals = {};
  const fouled = {};
  for (const uid of uids) {
    const arr = arrangements[uid];
    const validation = validateArrangement(arr);
    fouled[uid] = !validation.valid;
    evals[uid] = {
      bottom: evaluate5(arr.bottom),
      middle: evaluate5(arr.middle),
      top: evaluate3(arr.top),
    };
  }

  // Compare each pair of players
  for (let i = 0; i < uids.length; i++) {
    for (let j = i + 1; j < uids.length; j++) {
      const uid1 = uids[i];
      const uid2 = uids[j];

      let points1 = 0;
      let points2 = 0;
      let rowResults = [0, 0, 0]; // bottom, middle, top from uid1's perspective

      // Auto-win handling
      if (autoWinners.has(uid1) && !autoWinners.has(uid2)) {
        points1 = activeRules.autoWinValue || 1;
        points2 = -points1;
        rowResults = [1, 1, 1];
      } else if (autoWinners.has(uid2) && !autoWinners.has(uid1)) {
        points2 = activeRules.autoWinValue || 1;
        points1 = -points2;
        rowResults = [-1, -1, -1];
      } else if (autoWinners.has(uid1) && autoWinners.has(uid2)) {
        // Both auto-win = push
        points1 = 0;
        points2 = 0;
        rowResults = [0, 0, 0];
      } else {
        // Normal comparison
        // Fouled hands auto-lose
        if (fouled[uid1] && !fouled[uid2]) {
          rowResults = [-1, -1, -1];
        } else if (fouled[uid2] && !fouled[uid1]) {
          rowResults = [1, 1, 1];
        } else if (fouled[uid1] && fouled[uid2]) {
          rowResults = [0, 0, 0];
        } else {
          rowResults = [
            compareHands(evals[uid1].bottom, evals[uid2].bottom),
            compareHands(evals[uid1].middle, evals[uid2].middle),
            compareHands(evals[uid1].top, evals[uid2].top),
          ];
        }

        // Majority-based: count row wins
        const uid1Wins = rowResults.filter(r => r > 0).length;
        const uid2Wins = rowResults.filter(r => r < 0).length;

        if (uid1Wins > uid2Wins) {
          points1 = 1;
          points2 = -1;
        } else if (uid2Wins > uid1Wins) {
          points1 = -1;
          points2 = 1;
        }
        // else tie = 0, 0
      }

      results[uid1].breakdown[uid2] = { rows: rowResults, points: points1 };
      results[uid2].breakdown[uid1] = { rows: rowResults.map(r => -r), points: points2 };
      results[uid1].total += points1;
      results[uid2].total += points2;
    }
  }

  // Sweep bonus: win all 3 rows vs ALL opponents
  if (activeRules.sweepBonus) {
    for (const uid of uids) {
      const opponents = uids.filter(u => u !== uid);
      if (opponents.length < 2) continue; // Sweep requires 2+ opponents
      const sweepsAll = opponents.every(opp => {
        const br = results[uid].breakdown[opp];
        return br && br.rows.every(r => r > 0);
      });
      if (sweepsAll) {
        // Double: each opponent pays 2 instead of 1
        for (const opp of opponents) {
          results[uid].total += 1; // Add extra 1 (already had 1, now 2)
          results[opp].total -= 1;
          results[uid].breakdown[opp].points = 2;
          results[opp].breakdown[uid].points = -2;
        }
        results[uid].sweep = true;
      }
    }
  }

  // Individual round-house (Nem only): win all 3 rows vs ONE opponent = 2 from that player
  if (activeRules.individualRoundHouse) {
    for (const uid of uids) {
      for (const opp of uids) {
        if (uid === opp) continue;
        const br = results[uid].breakdown[opp];
        if (br && br.rows.every(r => r > 0) && br.points === 1) {
          // Already got 1, bump to 2
          results[uid].total += 1;
          results[opp].total -= 1;
          results[uid].breakdown[opp].points = 2;
          results[opp].breakdown[uid].points = -2;
        }
      }
    }
  }

  return results;
}

// ============================================================
// SCORING — CHI / NEM CHI (Per-row with bonuses)
// ============================================================

/**
 * Get the bonus value for a hand in a specific row position (Chi mode).
 * Bonus REPLACES base +1 win.
 */
function getChiBonus(evalResult, row, activeRules = {}) {
  if (!activeRules.bonusHandsEnabled) return 1; // All = 1 when bonuses disabled

  const { type } = evalResult;

  if (row === 'top') {
    if (type === TOP_HAND_TYPES.THREE_OF_A_KIND) return CHI_BONUSES.THREE_OF_A_KIND_TOP;
    return 1; // Regular win
  }

  const isMiddle = row === 'middle';
  const multiplier = (isMiddle && activeRules.middleMultiplierEnabled) ? 1 : 0;

  switch (type) {
    case HAND_TYPES.FIVE_OF_A_KIND:
      return isMiddle ? (multiplier ? CHI_BONUSES.FIVE_OF_A_KIND_MIDDLE : CHI_BONUSES.FIVE_OF_A_KIND_BOTTOM) : CHI_BONUSES.FIVE_OF_A_KIND_BOTTOM;
    case HAND_TYPES.ROYAL_FLUSH:
    case HAND_TYPES.STRAIGHT_FLUSH:
      return isMiddle ? (multiplier ? CHI_BONUSES.STRAIGHT_FLUSH_MIDDLE : CHI_BONUSES.STRAIGHT_FLUSH_BOTTOM) : CHI_BONUSES.STRAIGHT_FLUSH_BOTTOM;
    case HAND_TYPES.FOUR_OF_A_KIND:
      return isMiddle ? (multiplier ? CHI_BONUSES.FOUR_OF_A_KIND_MIDDLE : CHI_BONUSES.FOUR_OF_A_KIND_BOTTOM) : CHI_BONUSES.FOUR_OF_A_KIND_BOTTOM;
    case HAND_TYPES.FULL_HOUSE:
      return isMiddle ? CHI_BONUSES.FULL_HOUSE_MIDDLE : 1; // No bottom bonus for full house
    default:
      return 1; // Regular win
  }
}

/**
 * Score a round in Chi or NemChi mode (per-row with bonuses).
 *
 * @param {Object} arrangements - { uid: { bottom, middle, top } }
 * @param {Object} activeRules
 * @param {Object} originalHands - { uid: cards[] }
 * @returns {Object} { uid: { total, breakdown, sweep } }
 */
export function scoreChi(arrangements, activeRules = {}, originalHands = {}) {
  const uids = Object.keys(arrangements);
  const results = {};

  // Set defaults for active rules
  const rules = {
    bonusHandsEnabled: true,
    middleMultiplierEnabled: true,
    sweepBonus: true,
    individualRoundHouse: false,
    autoWinEnabled: false,
    autoWinValue: 3,
    ...activeRules,
  };

  // Initialize
  for (const uid of uids) {
    results[uid] = { total: 0, breakdown: {}, handValues: {} };
  }

  // Check auto-wins
  const autoWinners = new Set();
  if (rules.autoWinEnabled) {
    for (const uid of uids) {
      const original = originalHands[uid];
      if (original && checkSpecialHand(original).special) {
        autoWinners.add(uid);
      }
    }
  }

  // Evaluate all hands
  const evals = {};
  const fouled = {};
  for (const uid of uids) {
    const arr = arrangements[uid];
    const validation = validateArrangement(arr);
    fouled[uid] = !validation.valid;
    evals[uid] = {
      bottom: evaluate5(arr.bottom),
      middle: evaluate5(arr.middle),
      top: evaluate3(arr.top),
    };
  }

  // Compare each pair
  for (let i = 0; i < uids.length; i++) {
    for (let j = i + 1; j < uids.length; j++) {
      const uid1 = uids[i];
      const uid2 = uids[j];

      let points1 = 0;
      let points2 = 0;
      let rowResults = [0, 0, 0];
      let rowPoints1 = [0, 0, 0];
      let rowPoints2 = [0, 0, 0];

      // Auto-win handling
      if (autoWinners.has(uid1) && !autoWinners.has(uid2)) {
        points1 = rules.autoWinValue;
        points2 = -points1;
        rowResults = [1, 1, 1];
        rowPoints1 = [1, 1, 1];
        rowPoints2 = [-1, -1, -1];
      } else if (autoWinners.has(uid2) && !autoWinners.has(uid1)) {
        points2 = rules.autoWinValue;
        points1 = -points2;
        rowResults = [-1, -1, -1];
        rowPoints1 = [-1, -1, -1];
        rowPoints2 = [1, 1, 1];
      } else if (autoWinners.has(uid1) && autoWinners.has(uid2)) {
        points1 = 0; points2 = 0; rowResults = [0, 0, 0];
      } else {
        // Normal per-row comparison
        if (fouled[uid1] && !fouled[uid2]) {
          // uid1 fouled: loses all rows. uid2 gets their bonus values.
          rowResults = [-1, -1, -1];
          const b2 = getChiBonus(evals[uid2].bottom, 'bottom', rules);
          const m2 = getChiBonus(evals[uid2].middle, 'middle', rules);
          const t2 = getChiBonus(evals[uid2].top, 'top', rules);
          points2 = b2 + m2 + t2;
          points1 = -points2;
          rowPoints1 = [-b2, -m2, -t2];
          rowPoints2 = [b2, m2, t2];
        } else if (fouled[uid2] && !fouled[uid1]) {
          rowResults = [1, 1, 1];
          const b1 = getChiBonus(evals[uid1].bottom, 'bottom', rules);
          const m1 = getChiBonus(evals[uid1].middle, 'middle', rules);
          const t1 = getChiBonus(evals[uid1].top, 'top', rules);
          points1 = b1 + m1 + t1;
          points2 = -points1;
          rowPoints1 = [b1, m1, t1];
          rowPoints2 = [-b1, -m1, -t1];
        } else if (fouled[uid1] && fouled[uid2]) {
          points1 = 0; points2 = 0; rowResults = [0, 0, 0];
        } else {
          // Compare each row independently
          const rows = ['bottom', 'middle', 'top'];
          for (let r = 0; r < 3; r++) {
            const row = rows[r];
            const e1 = evals[uid1][row];
            const e2 = evals[uid2][row];
            const cmp = compareHands(e1, e2);
            rowResults[r] = cmp;

            if (cmp > 0) {
              const bonus = getChiBonus(e1, row, rules);
              points1 += bonus;
              points2 -= bonus;
              rowPoints1[r] = bonus;
              rowPoints2[r] = -bonus;
            } else if (cmp < 0) {
              const bonus = getChiBonus(e2, row, rules);
              points2 += bonus;
              points1 -= bonus;
              rowPoints1[r] = -bonus;
              rowPoints2[r] = bonus;
            }
            // Tie = 0 for that row
          }
        }
      }

      results[uid1].breakdown[uid2] = { rows: rowResults, rowPoints: rowPoints1, points: points1 };
      results[uid2].breakdown[uid1] = { rows: rowResults.map(r => -r), rowPoints: rowPoints2, points: points2 };
      results[uid1].total += points1;
      results[uid2].total += points2;
    }
  }

  // Calculate hand values for each player (for round-house doubling)
  for (const uid of uids) {
    if (!fouled[uid] && !autoWinners.has(uid)) {
      const b = getChiBonus(evals[uid].bottom, 'bottom', rules);
      const m = getChiBonus(evals[uid].middle, 'middle', rules);
      const t = getChiBonus(evals[uid].top, 'top', rules);
      results[uid].handValues = { bottom: b, middle: m, top: t, total: b + m + t };
    }
  }

  // Sweep / Round-house (all opponents): win all 3 rows vs ALL → double entire hand score
  // Requires 2+ opponents — sweeping a single opponent in 1v1 does not qualify
  if (rules.sweepBonus) {
    for (const uid of uids) {
      if (autoWinners.has(uid) || fouled[uid]) continue;
      const opponents = uids.filter(u => u !== uid && !autoWinners.has(u));
      if (opponents.length < 2) continue; // Sweep requires 2+ opponents

      const sweepsAll = opponents.every(opp => {
        const br = results[uid].breakdown[opp];
        return br && br.rows.every(r => r > 0);
      });

      if (sweepsAll) {
        // Double: recalculate as handValue × 2 from each opponent
        const handValue = results[uid].handValues.total || 0;
        for (const opp of opponents) {
          const oldPts = results[uid].breakdown[opp].points;
          const newPts = handValue * 2;
          const diff = newPts - oldPts;
          results[uid].total += diff;
          results[opp].total -= diff;
          results[uid].breakdown[opp].points = newPts;
          results[opp].breakdown[uid].points = -newPts;
        }
        results[uid].sweep = true;
      }
    }
  }

  // Individual round-house: win all 3 vs ONE opponent → double from that player
  if (rules.individualRoundHouse && !rules.sweepBonus) {
    // Only apply if sweep didn't already handle it
    for (const uid of uids) {
      if (autoWinners.has(uid) || fouled[uid]) continue;
      for (const opp of uids) {
        if (uid === opp || autoWinners.has(opp)) continue;
        const br = results[uid].breakdown[opp];
        if (br && br.rows.every(r => r > 0)) {
          const handValue = results[uid].handValues.total || 0;
          const oldPts = br.points;
          const newPts = handValue * 2;
          const diff = newPts - oldPts;
          results[uid].total += diff;
          results[opp].total -= diff;
          results[uid].breakdown[opp].points = newPts;
          results[opp].breakdown[uid].points = -newPts;
        }
      }
    }
  } else if (rules.individualRoundHouse && rules.sweepBonus) {
    // If both are on, individual applies to non-sweep cases
    for (const uid of uids) {
      if (autoWinners.has(uid) || fouled[uid] || results[uid].sweep) continue;
      for (const opp of uids) {
        if (uid === opp || autoWinners.has(opp)) continue;
        const br = results[uid].breakdown[opp];
        if (br && br.rows.every(r => r > 0)) {
          const handValue = results[uid].handValues.total || 0;
          const oldPts = br.points;
          const newPts = handValue * 2;
          const diff = newPts - oldPts;
          results[uid].total += diff;
          results[opp].total -= diff;
          results[uid].breakdown[opp].points = newPts;
          results[opp].breakdown[uid].points = -newPts;
        }
      }
    }
  }

  return results;
}

// ============================================================
// UNIFIED SCORING FUNCTION
// ============================================================

/**
 * Score a round based on game mode.
 * @param {string} gameMode - 'basic', 'nem', 'chi', 'nemchi'
 * @param {Object} arrangements - { uid: { bottom, middle, top } }
 * @param {Object} activeRules - Current toggleable rules
 * @param {Object} originalHands - { uid: cards[] } — for auto-win validation
 * @returns {Object} Scoring results
 */
export function scoreRound(gameMode, arrangements, activeRules = {}, originalHands = {}) {
  switch (gameMode) {
    case GAME_MODES.BASIC:
    case GAME_MODES.NEM:
      return scoreBasicNem(arrangements, activeRules, originalHands);
    case GAME_MODES.CHI:
    case GAME_MODES.NEM_CHI:
      return scoreChi(arrangements, activeRules, originalHands);
    default:
      throw new Error(`Unknown game mode: ${gameMode}`);
  }
}

// ============================================================
// CARD SORTING HELPERS (for UI sort buttons)
// ============================================================

/** Sort by rank A→K (ascending). */
export function sortHandByRank(cards) {
  return sortByRankAsc(cards);
}

/** Sort by suit, then rank within suit. */
export function sortHandBySuit(cards) {
  return sortBySuit(cards);
}

/** Group pairs together, then singles in rank order. */
export function sortHandByPairs(cards) {
  const groups = groupByRank(cards);
  const pairs = [];
  const singles = [];

  const entries = [...groups.entries()].sort((a, b) => b[0] - a[0]); // Descending rank
  for (const [, group] of entries) {
    if (group.length >= 2) {
      pairs.push(...group);
    } else {
      singles.push(...group);
    }
  }

  return [...pairs, ...singles.sort((a, b) => rankValue(b) - rankValue(a))];
}

// ============================================================
// HAND NAME HELPERS (for display)
// ============================================================

const HAND_TYPE_NAMES_5 = {
  [HAND_TYPES.HIGH_CARD]: 'High Card',
  [HAND_TYPES.ONE_PAIR]: 'One Pair',
  [HAND_TYPES.TWO_PAIR]: 'Two Pair',
  [HAND_TYPES.THREE_OF_A_KIND]: 'Three of a Kind',
  [HAND_TYPES.STRAIGHT]: 'Straight',
  [HAND_TYPES.FLUSH]: 'Flush',
  [HAND_TYPES.FULL_HOUSE]: 'Full House',
  [HAND_TYPES.FOUR_OF_A_KIND]: 'Four of a Kind',
  [HAND_TYPES.STRAIGHT_FLUSH]: 'Straight Flush',
  [HAND_TYPES.ROYAL_FLUSH]: 'Royal Flush',
  [HAND_TYPES.FIVE_OF_A_KIND]: 'Five of a Kind',
};

const HAND_TYPE_NAMES_3 = {
  [TOP_HAND_TYPES.HIGH_CARD]: 'High Card',
  [TOP_HAND_TYPES.ONE_PAIR]: 'One Pair',
  [TOP_HAND_TYPES.THREE_OF_A_KIND]: 'Three of a Kind',
};

/** Get human-readable name for a 5-card hand evaluation. */
export function getHandName5(evalResult) {
  return HAND_TYPE_NAMES_5[evalResult.type] || 'Unknown';
}

/** Get human-readable name for a 3-card hand evaluation. */
export function getHandName3(evalResult) {
  return HAND_TYPE_NAMES_3[evalResult.type] || 'Unknown';
}

/** Get the rank display string (e.g., "A", "K", "10"). */
export function rankDisplay(card) {
  return card.rank;
}

/** Get suit symbol. */
export function suitSymbol(card) {
  const symbols = { S: '♠', H: '♥', D: '♦', C: '♣' };
  return symbols[card.suit] || card.suit;
}

/** Get suit color. */
export function suitColor(card) {
  return (card.suit === 'H' || card.suit === 'D') ? 'red' : 'black';
}
